import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, AtomBadge, StatusPill, PriorityPill, CBNBadge } from "../components/primitives";
import { NetworkGraph, graphForSubject } from "../components/NetworkGraph";
import { formatNGN } from "../lib/format";
import jsPDF from "jspdf";

export default function AlertDetail() {
  const { aid } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/alerts/${aid}`).then(r => setData(r.data)); }, [aid]);

  if (!data) return <div className="p-6 text-[13px] text-ink-muted">Loading alert…</div>;
  const { alert: a, narrative, typology } = data;
  const graph = graphForSubject(a.subject_name, a.subject_customer, a.typology, a.amount_at_risk);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Examiner walkthrough — " + a.id, 14, 18);
    doc.setFontSize(10);
    doc.text(`Atom AML Solution · Generated ${new Date().toLocaleString()}`, 14, 25);
    doc.line(14, 28, 196, 28);
    doc.setFontSize(11);
    doc.text(`Subject: ${a.subject_name} (Cust ID ${a.subject_customer})`, 14, 38);
    doc.text(`Priority: ${a.priority}    Status: ${a.status}    Amount at risk: ${formatNGN(a.amount_at_risk)}`, 14, 45);
    doc.text(`Rule: ${a.rule}`, 14, 52);
    doc.text(`AMLTRIX: ${typology.amltrix} (${typology.name})`, 14, 59);
    doc.text(`CBN sections: ${(typology.cbn||[]).join(", ")}`, 14, 66);
    doc.text(`FATF: ${(typology.fatf||[]).join(", ")}`, 14, 73);
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(narrative, 180);
    doc.text(lines, 14, 84);
    doc.save(`alert-${a.id}-walkthrough.pdf`);
  };

  return (
    <div>
      <PageHeader
        title={`${a.id} · ${a.subject_name}`}
        subtitle={`${a.rule} · Raised ${a.raised}`}
        sections={a.cbn}
        atom={<AtomBadge model="Atom Nucleus" version="v3.5" />}
        right={<div className="flex gap-2"><button onClick={exportPDF} className="px-3 py-1.5 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Examiner walkthrough · PDF</button></div>}
      />

      <div className="p-6 grid grid-cols-3 gap-3">
        <Card className="col-span-2">
          <CardHeader title="Atom Nucleus narrative" subtitle="Plain-English reasoning for examination handover" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
          <div className="p-5 border-l-2 border-amber/40 bg-amber/4 ml-5 my-5 mr-5 text-[13px] leading-relaxed whitespace-pre-wrap">{narrative}</div>
          <div className="px-5 pb-5 text-[11px] text-ink-subtle">
            This alert satisfies CBN Section(s) {a.cbn.join(", ")} · operationalises FATF {(typology.fatf||[]).join(", ")} · Atom Nucleus v3.5 explainability 0.92 (Atom Photon v1.0 assessment).
          </div>
        </Card>

        {/* Network analysis */}
        <Card className="col-span-2">
          <CardHeader title="Network analysis" subtitle="Counterparty, sanctions, PEP and related-case relationships · hover to inspect" right={<AtomBadge model="Atom Nucleus" version="v3.5" />} />
          <div className="p-3">
            <NetworkGraph subject={graph.subject} nodes={graph.nodes} edges={graph.edges} />
          </div>
        </Card>

        <div className="col-span-1 space-y-3">
          <Card>
            <CardHeader title="Alert summary" />
            <div className="p-3 text-[12px] space-y-1.5">
              <div className="flex justify-between"><span className="text-ink-subtle">Priority</span><PriorityPill priority={a.priority} /></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Status</span><StatusPill status={a.status} /></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Subject</span><Link to={`/customers/${a.subject_customer}`} className="text-amber-600 hover:underline">{a.subject_name}</Link></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Rule</span><span className="text-right">{a.rule}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Typology</span><span className="text-right">{typology.amltrix}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Tactic</span><span>{typology.tactic}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Amount at risk</span><span className="tabular font-medium">{formatNGN(a.amount_at_risk)}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Assignee</span><span>{a.assignee}</span></div>
              <div className="flex justify-between"><span className="text-ink-subtle">Raised</span><span>{a.raised}</span></div>
            </div>
          </Card>
          <Card>
            <CardHeader title="Recommended actions" />
            <div className="p-3 space-y-2">
              <button className="w-full text-left px-3 py-2 text-[12px] bg-navy text-white rounded-sm hover:bg-navy-700">Create case → MLRO</button>
              <button className="w-full text-left px-3 py-2 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Draft STR</button>
              <button className="w-full text-left px-3 py-2 text-[12px] border border-line rounded-sm hover:bg-bg-hover">Network analysis</button>
              <Link to="/atom-explainer" className="block w-full text-left px-3 py-2 text-[12px] border border-amber/40 bg-amber/8 text-amber-600 rounded-sm hover:bg-amber/15">Open in Atom Explainer →</Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
